/**
 * What counts as a usable email address and an acceptable password.
 *
 * Here rather than in the API because three places need the same answer and
 * must not disagree: the sign-up form in the browser, the server that accepts
 * it, and the reset page served by the API to somebody who opened their email
 * on a phone. A password the form accepts and the server rejects is a form
 * that lies.
 *
 * Every function returns a sentence a person can act on, or null for "fine".
 * Not a boolean, and not an error code — the whole value of validating in the
 * browser is telling somebody *what* to change, and a code would have to be
 * translated back into a sentence somewhere, which is where the two copies
 * drift apart.
 */
/**
 * Twelve, not eight.
 *
 * Eight characters with mixed case and a digit is a rule that produces
 * `Passw0rd!` — memorable to nobody, guessable by everyone. Length is the
 * property that actually resists guessing, so this asks for length and does
 * not demand a symbol. NIST stopped recommending composition rules years ago
 * for exactly this reason.
 */
export const MIN_PASSWORD_LENGTH = 12;
/**
 * And an upper bound, because scrypt hashes whatever it is given and a
 * megabyte of input is a free way to tie up a server.
 */
export const MAX_PASSWORD_LENGTH = 200;
/**
 * Passwords nobody should be allowed to choose, however long.
 *
 * Deliberately tiny. A real breach-corpus check belongs at the edge of a much
 * bigger system; this is the short list that shows up in practice when
 * somebody is told "at least twelve characters" and types the first thing that
 * satisfies it.
 */
const OBVIOUS = [
    'password',
    'passw0rd',
    'qwerty',
    'letmein',
    'welcome',
    'iloveyou',
    'admin',
    'crewlark',
];
/** Lowercased and trimmed, which is how every lookup must spell it. */
export function normalizeEmail(email) {
    return email.trim().toLowerCase();
}
/**
 * Good enough to send mail to.
 *
 * Deliberately permissive. The grammar of a real address is far stranger than
 * most validators admit, and the cost of wrongly rejecting a legitimate
 * address is somebody who cannot sign up at all. The only real proof an
 * address works is a message arriving at it.
 */
export function emailProblem(email) {
    const value = normalizeEmail(email);
    if (!value)
        return 'Enter your email address.';
    if (value.length > 254)
        return 'That email address is too long.';
    const at = value.lastIndexOf('@');
    if (at <= 0 || at === value.length - 1)
        return 'That does not look like an email address.';
    const domain = value.slice(at + 1);
    if (!domain.includes('.') || domain.startsWith('.') || domain.endsWith('.')) {
        return 'That does not look like an email address.';
    }
    if (/\s/.test(value))
        return 'An email address cannot contain spaces.';
    return null;
}
/** Null when the password is acceptable; otherwise what is wrong with it. */
export function passwordProblem(password, context = []) {
    if (!password)
        return 'Choose a password.';
    if (password.length < MIN_PASSWORD_LENGTH) {
        return `Use at least ${MIN_PASSWORD_LENGTH} characters. A short phrase works well.`;
    }
    if (password.length > MAX_PASSWORD_LENGTH) {
        return `Keep it under ${MAX_PASSWORD_LENGTH} characters.`;
    }
    const folded = password.toLowerCase();
    if (OBVIOUS.some((bad) => folded.includes(bad))) {
        return 'That is one of the first passwords anyone would try. Pick something else.';
    }
    /*
     * A password built out of the name and email on the same form is the other
     * thing people reach for under a length requirement, and it is the first
     * thing anyone targeting this account would try.
     */
    for (const hint of context) {
        const part = normalizeEmail(hint).split('@')[0] ?? '';
        if (part.length >= 4 && folded.includes(part)) {
            return 'Do not build the password out of your name or email address.';
        }
    }
    /* One repeated character, however many times, is one character. */
    if (new Set(password).size < 5) {
        return 'That password repeats too few different characters.';
    }
    return null;
}
/** Every field's first problem, keyed by field, for a form to render in place. */
export function signupProblems(details) {
    const problems = {};
    const companyName = details.companyName.trim();
    if (!companyName)
        problems.companyName = 'What is the business called?';
    else if (companyName.length < 2)
        problems.companyName = 'That name is too short.';
    else if (companyName.length > 120)
        problems.companyName = 'That name is too long.';
    const name = details.name.trim();
    if (!name)
        problems.name = 'Enter your name.';
    else if (name.length > 120)
        problems.name = 'That name is too long.';
    const email = emailProblem(details.email);
    if (email)
        problems.email = email;
    const password = passwordProblem(details.password, [details.email, details.name]);
    if (password)
        problems.password = password;
    return problems;
}
/**
 * How long a reset link is good for.
 *
 * An hour is the usual answer and it is the right one: long enough to survive
 * a slow mail server and somebody finishing their lunch, short enough that a
 * link left in an inbox is not a standing key to the account.
 */
export const RESET_TOKEN_TTL_MINUTES = 60;
export function resetTokenExpiry(now) {
    return new Date(now.getTime() + RESET_TOKEN_TTL_MINUTES * 60_000);
}
/**
 * Why a reset link cannot be used, in words, or null if it can.
 *
 * Used twice, which is the point: once when the page is opened, and again when
 * the new password is submitted. A link that expires between those two moments
 * must fail the second check as well, and a single rule means it cannot be
 * right in one place and wrong in the other.
 */
export function resetTokenProblem(token, now) {
    if (!token)
        return 'That link is not valid. Ask for a new one.';
    if (token.usedAt)
        return 'That link has already been used. Ask for a new one.';
    const expiresAt = typeof token.expiresAt === 'string' ? new Date(token.expiresAt) : token.expiresAt;
    if (expiresAt.getTime() <= now.getTime())
        return 'That link has expired. Ask for a new one.';
    return null;
}
//# sourceMappingURL=credentials.js.map